import React, { useEffect, useState } from 'react';
import { SinglePie } from './SinglePie';
import { baseURL } from '../services/axios';

interface AllCakesProps {
  childName: string;
  sessionId: string;
}

interface CakesData {
  behaviors: { [key: string]: number };
  path: { [key: string]: number };
  response: { [key: string]: number };
  totalAnalyses: number;
}

export const AllCakes: React.FC<AllCakesProps> = ({ childName, sessionId }) => {
  const [data, setData] = useState<CakesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      console.log(sessionId);
      try {
        setLoading(true);
        const res = await fetch(`${baseURL}/calculatePercentages/AnalyzeSession?sessionId=${sessionId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });

        if (!res.ok) throw new Error('Failed to fetch');

        const result = await res.json();
        const total = (Object.values(result?.response ?? {}) as number[]).reduce((sum, val) => sum + val, 0);

        setData({
          behaviors: result.behaviors || {},
          path: result.path || {},
          response: result.response || {},
          totalAnalyses: total
        });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    if (sessionId) fetchData();
  }, [sessionId]);

  // Convert data to format expected by SinglePie
  const convertToChartData = (dataObj: { [key: string]: number }) => {
    return Object.entries(dataObj).map(([name, value]) => ({
      name,
      value
    }));
  };

  if (loading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '2rem',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        <div style={{ 
          display: 'inline-block',
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '1rem'
        }}></div>
        <p>טוען נתונים...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '2rem',
        color: '#e74c3c',
        backgroundColor: '#ffeaa7',
        borderRadius: '8px',
        margin: '1rem'
      }}>
        <h3>שגיאה בטעינת הנתונים</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '2rem',
        color: '#666'
      }}>
        <p>לא נמצאו נתונים</p>
      </div>
    );
  }

  // Color schemes for each chart
  const behaviorsColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'];
  const pathColors = ['#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'];
  const responseColors = ['#FF8A65', '#81C784', '#64B5F6', '#FFB74D', '#A1887F'];

  return (
    <div style={{ 
      padding: '2rem',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '2rem'
      }}>
        <h2 style={{ 
          color: '#2c3e50',
          fontSize: '2rem',
          marginBottom: '0.5rem'
        }}>
          ניתוח התנהגות - {childName}
        </h2>
        <p style={{ 
          color: '#7f8c8d',
          fontSize: '1.1rem'
        }}>
          סה"כ ניתוחים: {data.totalAnalyses}
        </p>
      </div>

      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '2rem',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        <SinglePie 
          title="🟢 התנהגויות"
          data={convertToChartData(data.behaviors)}
          colors={behaviorsColors}
        />
        
        <SinglePie 
          title="🟡 נסיבות"
          data={convertToChartData(data.path)}
          colors={pathColors}
        />
        
        <SinglePie 
          title="🔴 תוצאות"
          data={convertToChartData(data.response)}
          colors={responseColors}
        />
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .grid-container {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};
